import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

// Define all formats you expect to receive
const SUPPORTED_FORMATS = [
  // Standard formats
  "YYYY-MM-DD",
  "MM-DD-YYYY",
  "DD-MM-YYYY",
  "YYYY/MM/DD",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY.MM.DD",
  "MM.DD.YYYY",
  "DD.MM.YYYY",
  
  // Month name formats
  "DD MMM YYYY",
  "MMM DD, YYYY",
  "DD/MMM/YYYY",
  "DD-MMM-YYYY",
  "YYYY-MMM-DD",
  "MMM-DD-YYYY",
  
  // Short year formats
  "DD/MM/YY",
  "MM/DD/YY",
  "YY/MM/DD",
  
  // ISO and other standards
  "YYYY-MM-DDTHH:mm:ss.SSSZ", // ISO format
  "YYYY-MM-DDTHH:mm:ssZ",
];

/**
 * Normalize any incoming date string to YYYY-MM-DD
 * @param dateStr - The input date string in any known format
 * @returns Normalized date string in YYYY-MM-DD
 * @throws Error if the date is invalid or unsupported
 */
export function normalizeDate(dateStr: string): string {
  if (!dateStr) return "";

  // First attempt: Try with strict parsing using supported formats
  let parsed = dayjs(dateStr, SUPPORTED_FORMATS, true);
  
  // Second attempt: If strict parsing fails, try non-strict parsing with supported formats
  if (!parsed.isValid()) {
    parsed = dayjs(dateStr, SUPPORTED_FORMATS);
  }
  
  // Third attempt: Try dayjs's built-in parser as a fallback
  if (!parsed.isValid()) {
    parsed = dayjs(dateStr);
  }

  // If all parsing attempts fail
  if (!parsed.isValid()) {
    console.error(`Failed to parse date: ${dateStr}`);
    
    // Attempt a manual parse for formats like "DD/MMM/YYYY"
    try {
      const parts = dateStr.split(/[\/\-\.]/);
      if (parts.length === 3) {
        const months = {
          jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, 
          jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
        };
        
        let day, month, year;
        
        // Check if middle part is a month name
        const monthLower = parts[1].toLowerCase();
        if (monthLower in months) {
          day = parseInt(parts[0]);
          month = months[monthLower];
          year = parseInt(parts[2]);
          parsed = dayjs(new Date(year, month, day));
        } 
        // Try other common patterns
        else {
          parsed = dayjs(new Date(dateStr));
        }
      }
    } catch (e) {
      console.error("Manual date parsing failed:", e);
    }
  }
  
  // Final check
  if (!parsed.isValid()) {
    throw new Error(`Invalid date format: ${dateStr}`);
  }

  return parsed.format("YYYY-MM-DD");
}

/**
 * Format a time string (HH:mm:ss or HH:mm) to a consistent display format
 * @param timeStr - The input time string
 * @param outputFormat - The desired output format (default: 'h:mm A')
 * @returns Formatted time string
 */
export function formatTime(timeStr: string, outputFormat: string = "h:mm A"): string {
  if (!timeStr) return "";
  
  // Try common time formats
  const formats = ["HH:mm:ss", "HH:mm", "h:mm A", "h:mm a", "H:mm"];
  let parsed = dayjs(timeStr, formats, true);
  
  if (!parsed.isValid()) {
    parsed = dayjs(timeStr, formats);
  }
  
  if (!parsed.isValid()) {
    // Fallback to dayjs's natural parsing or just return original
    const nativeParsed = dayjs(timeStr);
    if (nativeParsed.isValid()) return nativeParsed.format(outputFormat);
    return timeStr;
  }
  
  return parsed.format(outputFormat);
}

/**
 * Format a "MM-DD" or similar string to "MMM DD" (e.g. "01-02" -> "Jan 02")
 */
export function formatMonthDay(dateStr: string): string {
  if (!dateStr || dateStr === 'MM-DD') return dateStr;
  
  // Try to parse "MM-DD"
  const parsed = dayjs(dateStr, ["MM-DD", "YYYY-MM-DD", "MM/DD", "DD-MM", "DD/MM"], true);
  if (parsed.isValid()) {
    return parsed.format("MMM DD");
  }
  
  // Fallback
  const fallback = dayjs(dateStr);
  if (fallback.isValid()) {
    return fallback.format("MMM DD");
  }
  
  return dateStr;
}